import React, {useState, useRef, ChangeEvent, useEffect, useCallback, useMemo} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styles from './CreateProjectPopup.module.scss';
import {
  IconPlus,
  IconFileUpload,
  IconFileInfo,
  IconCheck,
    IconTemplate,
  IconX,
  IconChevronDown,
  IconLoader2
} from '@tabler/icons-react';
import {
  ProjectDto,
  ProjectType,
  Methods,
  Complexity,
  Status,
  SprintFrequency,
  TimingFactor,
  IndustrySector,
  ProjectCatalogDto
} from '../../api/model';
import { useToast } from '../ui/Toast/ToastProvider';
import InfoHint from '../ui/InfoHint/InfoHint';
import { usePostProject } from '../../api/project-api/project-api';
import { useProjectCatalog, useProjects } from '../../hooks/useProjects';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import { useGetAuthUser } from '../../api/auth-api/auth-api';
import {useGetAdminLicenses} from '../../api/admin-api/admin-api';
import {useGetTemplate, usePostTemplateIdInstantiate} from '../../api/template-api/template-api';
import type {InstantiateTemplateDto, TemplateDTO} from '../../api/model';
import TextLimitHint from '../../components/ui/TextLimitHint/TextLimitHint';
import { LONG_DESCRIPTION } from '../../utils/textLimits';

interface CreateProjectPopupProps {
  onClose: () => void;
  onSuccess?: (project: ProjectDto) => void;
    initialTemplateId?: number;
}

interface ProjectFormData {
  name: string;
  description: string;
  projectType: ProjectType | '';
  projectTypeUid: string;
  method: Methods | '';
  complexity: Complexity | '';
  startTime: string;
  endTime: string;
  timingFactor: TimingFactor | '';
  icon: File | null;
  sprintFrequency: SprintFrequency | '';
  firstSprintStartDate: string;
}

const CreateProjectPopup: React.FC<CreateProjectPopupProps> = ({onClose, onSuccess, initialTemplateId}) => {
  const { t, i18n } = useTranslation('project');
  const lang: 'en' | 'de' = i18n.language?.toLowerCase().startsWith('de') ? 'de' : 'en';

  const getProjectType = (item: ProjectCatalogDto) => lang === 'de' ? item.projectTypeDe : item.projectTypeEn;

  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    projectType: '',
    projectTypeUid: '',
    method: '',
    complexity: '',
    startTime: '',
    endTime: '',
    timingFactor: '',
    icon: null,
    sprintFrequency: '',
    firstSprintStartDate: ''
  });

  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const { isSystemAdmin } = useCurrentUserPermissions();
  const { data: authUser } = useGetAuthUser();
  const {data: licenses = []} = useGetAdminLicenses({
    query: {
      staleTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: false,
    },
  });
  const {
    data: existingProjects = [],
    isFetching: isProjectsFetching,
    refetch: refetchProjects,
  } = useProjects();
    const {data: templates = []} = useGetTemplate({
        query: {
            staleTime: 1000 * 60 * 10,
            refetchOnWindowFocus: false,
        },
    });

  const resolvedLicense = useMemo(() => {
    if (isSystemAdmin || !authUser?.licenseType) return undefined;
    return licenses.find(license => license.active !== false && license.licenseType === authUser.licenseType)
        ?? licenses.find(license => license.licenseType === authUser.licenseType);
  }, [authUser?.licenseType, isSystemAdmin, licenses]);

  useEffect(() => {
    void refetchProjects();
  }, [refetchProjects]);

  const maxProjects = typeof resolvedLicense?.maxProjects === 'number' ? resolvedLicense.maxProjects : -1;
  const atProjectLimit = !isProjectsFetching && maxProjects !== -1 && existingProjects.length >= maxProjects;

  const createProjectMutation = usePostProject({
    mutation: {
      onSuccess: (createdProject) => {
        showSuccess(
          'Project Created!', 
          `Project "${createdProject.name}" has been created successfully.`
        );
        
        // invalidate projects cache to refetch list
        queryClient.invalidateQueries({queryKey: ['projects']});
        queryClient.invalidateQueries({ queryKey: ['/project'] });
        
        onSuccess?.(createdProject);
        onClose();
      },
      onError: (error: unknown) => {
        showError(
          'Failed to Create Project',
            error instanceof Error ? error.message : 'An error occurred while creating the project'
        );
      }
    }
  });
    const instantiateTemplateMutation = usePostTemplateIdInstantiate({
        mutation: {
            onSuccess: (createdProject) => {
                showSuccess(
                    'Project Created!',
                    `Project "${createdProject.name}" has been created successfully from the selected template.`
                );
              queryClient.invalidateQueries({queryKey: ['projects']});
                queryClient.invalidateQueries({queryKey: ['/project']});
                onSuccess?.(createdProject);
                onClose();
            },
          onError: (error: unknown) => {
                showError(
                    'Failed to Create Project',
                    error instanceof Error ? error.message : 'An error occurred while creating the project from template'
                );
            },
        },
    }, queryClient);
  const { data: projectCatalog = [], isLoading: catalogLoading } = useProjectCatalog();

  const isBlankProject = formData.projectTypeUid === 'B01';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(initialTemplateId ?? null);
    const availableTemplates = templates.filter((template): template is TemplateDTO & {
        id: number
    } => template.id != null);
    const selectedTemplate = availableTemplates.find((template) => template.id === selectedTemplateId);

  // Handle click outside to close
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

    useEffect(() => {
        if (!initialTemplateId || templates.length === 0) return;
        const template = templates.find((item) => item.id === initialTemplateId);
        if (!template) return;
        setSelectedTemplateId(initialTemplateId);
        setFormData((prev) => applyTemplateDefaults(prev, template));
    }, [initialTemplateId, templates]);

    const applyTemplateDefaults = (prev: ProjectFormData, template: TemplateDTO): ProjectFormData => {
        const next = {...prev};

        if (template.name && !next.name.trim()) {
            next.name = template.name;
        }
        if (template.description && !next.description.trim()) {
            next.description = template.description;
        }
        next.method = (template.method ?? '') as Methods | '';
        next.complexity = (template.complexity ?? '') as Complexity | '';
        next.timingFactor = (template.timingFactor ?? '') as TimingFactor | '';
        next.sprintFrequency = (template.sprintFrequency ?? '') as SprintFrequency | '';

        return next;
    };

    const handleTemplateChange = (value: string) => {
        const templateId = value ? Number(value) : null;
        setSelectedTemplateId(templateId);

        if (!templateId) return;

        const template = templates.find((item) => item.id === templateId);
        if (!template) return;

        setFormData((prev) => applyTemplateDefaults(prev, template));
    };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // handling project type
    if (name === 'projectType' && value) {
      const selectedCatalogItem = projectCatalog.find(item => getProjectType(item) === value);
      
      setFormData(prev => ({
        ...prev,
        [name]: value as ProjectType | '',
        projectTypeUid: selectedCatalogItem?.uid || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/svg+xml')) {
      if (file.size <= 2 * 1024 * 1024) { // 2 MB limit for file upload. We make sure file is appropriate
        setFormData(prev => ({
          ...prev,
          icon: file
        }));
      } else {
        showError('File Too Large', 'Image file size must be less than 2MB');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } else {
      showError('Invalid File', 'Please select a PNG, JPEG or SVG image');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      icon: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // func to get sprint duration in days
  const getSprintDurationInDays = (sprintFrequency: SprintFrequency): number => {
    switch (sprintFrequency) {
      case SprintFrequency.WEEKLY_1:
        return 7;
      case SprintFrequency.WEEKLY_2:
        return 14;
      case SprintFrequency.WEEKLY_3:
        return 21;
      case SprintFrequency.WEEKLY_5:
        return 35;
      case SprintFrequency.MONTHLY:
        return 30;
      default:
        return 0;
    }
  };

  // Validate form data
  const validateFormData = (): boolean => {
      const isTemplateMode = selectedTemplateId != null;

    // Check required fields
    if (!formData.name.trim()) {
      showError(t('create.validation.warning'), t('create.validation.nameRequired'));
      return false;
    }
    
    if (formData.name.length > 50) {
      showError(t('create.validation.warning'), t('create.validation.nameMaxLength'));
      return false;
    }
    

      if (isTemplateMode) {
          if (formData.startTime && formData.endTime) {
              const startDate = new Date(formData.startTime);
              const endDate = new Date(formData.endTime);
              if (endDate < startDate) {
                  showError(t('create.validation.warning'), t('create.validation.endAfterStart'));
                  return false;
              }
          }

          if (formData.method === Methods.AGILE || formData.method === Methods.HYBRID) {
              if (formData.firstSprintStartDate && formData.startTime && new Date(formData.firstSprintStartDate) < new Date(formData.startTime)) {
                  showError(t('create.validation.warning'), t('create.validation.firstSprintBeforeStart'));
                  return false;
              }
              if (formData.firstSprintStartDate && formData.endTime && new Date(formData.firstSprintStartDate) > new Date(formData.endTime)) {
                  showError(t('create.validation.warning'), t('create.validation.firstSprintAfterEnd'));
                  return false;
              }
          }

          return true;
      }

    if (!formData.projectType) {
      showError(t('create.validation.warning'), t('create.validation.typeRequired'));
      return false;
    }
    
    if (!formData.projectTypeUid) {
      showError(t('create.validation.warning'), t('create.validation.typeUidMissing'));
      return false;
    }

    // Blank Project skips some create validations
    if (!isBlankProject) {
      if (!formData.method) {
        showError(t('create.validation.warning'), t('create.validation.methodRequired'));
        return false;
      }
      if ((formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && !formData.sprintFrequency) {
        showError(t('create.validation.warning'), t('create.validation.sprintDurationRequired'));
        return false;
      }
      if ((formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && !formData.firstSprintStartDate) {
        showError(t('create.validation.warning'), t('create.validation.firstSprintRequired'));
        return false;
      }

      if ((formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && formData.firstSprintStartDate && formData.startTime && formData.endTime) {
        const projectStartDate = new Date(formData.startTime);
        const projectEndDate = new Date(formData.endTime);
        const sprintStartDate = new Date(formData.firstSprintStartDate);

        if (sprintStartDate < projectStartDate) {
          showError(t('create.validation.warning'), t('create.validation.firstSprintBeforeStart'));
          return false;
        }

        if (sprintStartDate > projectEndDate) {
          showError(t('create.validation.warning'), t('create.validation.firstSprintAfterEnd'));
          return false;
        }

        if (formData.sprintFrequency) {
          const sprintDurationDays = getSprintDurationInDays(formData.sprintFrequency as SprintFrequency);
          const minProjectEndDate = new Date(sprintStartDate);
          minProjectEndDate.setDate(minProjectEndDate.getDate() + sprintDurationDays + 1);

          if (projectEndDate < minProjectEndDate) {
            const formatter = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            showError(
              t('create.validation.warning'),
              t('create.validation.endDateMin', { date: formatter.format(minProjectEndDate) })
            );
            return false;
          }
        }
      }

      if (!formData.complexity) {
        showError(t('create.validation.warning'), t('create.validation.complexityRequired'));
        return false;
      }
      if (!formData.startTime) {
        showError(t('create.validation.warning'), t('create.validation.startRequired'));
        return false;
      }
      if (!formData.endTime) {
        showError(t('create.validation.warning'), t('create.validation.endRequired'));
        return false;
      }
      if (!formData.timingFactor) {
        showError(t('create.validation.warning'), t('create.validation.timingRequired'));
        return false;
      }

      const startDate = new Date(formData.startTime);
      const endDate = new Date(formData.endTime);

      if (endDate < startDate) {
        showError(t('create.validation.warning'), t('create.validation.endAfterStart'));
        return false;
      }
      if (startDate.getTime() === endDate.getTime()) {
        showError(t('create.validation.warning'), t('create.validation.sameDates'));
        return false;
      }
      const yearsDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      if (yearsDiff > 10) {
        showError(t('create.validation.warning'), t('create.validation.maxDuration'));
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateFormData()) {
      return;
    }
    
    try {
        if (selectedTemplateId != null) {
          const templateData: InstantiateTemplateDto = {
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                startTime: formData.startTime || undefined,
                endTime: formData.endTime || undefined,
            languageCode: lang,
                complexity: formData.complexity || undefined,
                method: formData.method || undefined,
                timingFactor: formData.timingFactor || undefined,
                sprintFrequency: formData.sprintFrequency || undefined,
                firstSprintStartDate: formData.firstSprintStartDate || undefined,
            };

            instantiateTemplateMutation.mutate({
                id: selectedTemplateId,
                data: templateData,
            });
            return;
        }

      const projectData: ProjectDto = {
        name: formData.name.trim(),
        projectType: formData.projectType as ProjectType,
        projectTypeUid: formData.projectTypeUid,
        languageCode: lang,
        status: Status.PLANNED,
        progress: 0
      };

      if (!isBlankProject) {
        projectData.method = formData.method as Methods;
        projectData.complexity = formData.complexity as Complexity;
        projectData.startTime = formData.startTime;
        projectData.endTime = formData.endTime;

        if ((formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && formData.sprintFrequency) {
          projectData.sprintFrequency = formData.sprintFrequency as SprintFrequency;
        }
        if ((formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && formData.firstSprintStartDate) {
          projectData.firstSprintStartDate = formData.firstSprintStartDate;
        }
        if (formData.timingFactor) {
          projectData.timingFactor = formData.timingFactor as TimingFactor;
        }
      }

      // Add description to project data if provided
      if (formData.description.trim()) {
        projectData.description = formData.description.trim();
      }

      // Handle icon upload
      if (formData.icon) {
        try {
          const fileReader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
            fileReader.onloadend = () => {
              if (typeof fileReader.result === 'string') {
                resolve(fileReader.result);
              } else {
                reject(new Error('Failed to read file'));
              }
            };
            fileReader.onerror = () => reject(new Error('Failed to read file'));
            fileReader.readAsDataURL(formData.icon!);
          });
          
          const base64String = await base64Promise;
          projectData.icon = base64String;
        } catch (error) {
          showError('Icon Error', 'Failed to process the selected icon, but will continue with project creation');
          console.error('Icon error', error);
        }
      }
      
      createProjectMutation.mutate({ data: projectData });

    } catch {
      showError('Error', 'An unexpected error occurred while preparing project data');
    }
  };

  const isSubmitDisabled =
    atProjectLimit ||
      isProjectsFetching ||
    !formData.name ||
      (selectedTemplateId == null && (!formData.projectType || !formData.projectTypeUid)) ||
      (selectedTemplateId == null ? createProjectMutation.isPending : instantiateTemplateMutation.isPending) ||
    catalogLoading ||
      (selectedTemplateId == null && !isBlankProject && (
      !formData.method ||
      ((formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && !formData.sprintFrequency) ||
      ((formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && !formData.firstSprintStartDate) ||
      !formData.complexity ||
      !formData.startTime ||
      !formData.endTime ||
      !formData.timingFactor
    ));

  if (isSystemAdmin) {
    return (
      <div className={styles.overlay} onClick={handleOverlayClick}>
        <div className={styles.popup} onClick={e => e.stopPropagation()}>
          <div className={styles.header}>
            <div className={styles.headerTop}>
              <h2 className={styles.title}>{t('create.title')}</h2>
              <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                <IconX size={20} />
              </button>
            </div>
          </div>
          <div className={styles.content}>
            <p style={{ margin: 0, fontSize: 14, color: '#525866', lineHeight: 1.6 }}>
              {t('create.systemAdminBlocked')}
            </p>
          </div>
          <div className={styles.footer}>
            <button className={styles.cancelButton} onClick={onClose}>
              <span>{t('create.buttons.cancel')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.popup} ref={popupRef} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <div className={styles.headerTop}>
              <h2 className={styles.title}>{t('create.title')}</h2>
              <button className={styles.closeButton} onClick={onClose} aria-label="Close popup">
                <IconX size={20} />
              </button>
            </div>
          </div>
        </div>

        {atProjectLimit && (
          <div className={styles.limitBanner}>
            {t('create.licenseLimit.reached', {
              current: existingProjects.length,
              max: maxProjects,
            })}
          </div>
        )}

        <div className={styles.content}>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('create.fields.name')}</label>
            <input
              type="text"
              name="name"
              className={`${styles.input} ${formData.name ? styles.hasContent : ''}`}
              placeholder={t('create.fields.namePlaceholder')}
              value={formData.name}
              onChange={handleInputChange}
              maxLength={50}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>{t('create.fields.description')}</label>
            <textarea
              name="description"
              className={`${styles.input} ${styles.textarea} ${formData.description ? styles.hasContent : ''}`}
              placeholder={t('create.fields.descriptionPlaceholder')}
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              maxLength={LONG_DESCRIPTION}
            />
            <TextLimitHint value={formData.description} max={LONG_DESCRIPTION} />
          </div>

          <div className={styles.formGroup}>
              <label className={styles.label}>{t('create.template.fields.template')}</label>
              <div className={styles.selectWrapper}>
                  <select
                      value={selectedTemplateId ?? ''}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      className={`${styles.input} ${selectedTemplateId ? styles.hasContent : ''}`}
                  >
                      <option value="">{t('create.template.fields.templateNone')}</option>
                      {availableTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                              {template.name || `${t('create.template.fallback')} #${template.id}`}
                          </option>
                      ))}
                  </select>
                  <div className={styles.iconWrapper}>
                      <IconTemplate color='#525866'/>
                  </div>
              </div>
              <div className={styles.fieldHint}>
                  {t('create.template.hint')}
              </div>
          </div>

            {selectedTemplate && (
                <div className={styles.templateSummary}>
                    <div className={styles.templateSummaryHeader}>
                <span className={styles.templateSummaryTitle}>
                  {selectedTemplate.name || t('create.template.fallback')}
                </span>
                        {selectedTemplate.sourceProjectId && (
                            <span className={styles.templateSummaryMeta}>
                    {t('create.template.sourceProject', {id: selectedTemplate.sourceProjectId})}
                  </span>
                        )}
                    </div>
                    <div className={styles.templateSummaryGrid}>
                        <div>
                            <span className={styles.templateSummaryLabel}>{t('create.template.summary.method')}</span>
                            <span
                                className={styles.templateSummaryValue}>{selectedTemplate.method || t('create.template.summary.na')}</span>
                        </div>
                        <div>
                            <span
                                className={styles.templateSummaryLabel}>{t('create.template.summary.complexity')}</span>
                            <span
                                className={styles.templateSummaryValue}>{selectedTemplate.complexity || t('create.template.summary.na')}</span>
                        </div>
                        <div>
                            <span className={styles.templateSummaryLabel}>{t('create.template.summary.sprint')}</span>
                            <span
                                className={styles.templateSummaryValue}>{selectedTemplate.sprintFrequency || t('create.template.summary.na')}</span>
                        </div>
                        <div>
                            <span
                                className={styles.templateSummaryLabel}>{t('create.template.summary.structure')}</span>
                            <span className={styles.templateSummaryValue}>
                    {selectedTemplate.content?.phases?.length ?? 0} {t('create.template.summary.phases')}
                  </span>
                        </div>
                    </div>
                </div>
            )}

            {!selectedTemplateId && (
                <div className={styles.formGroup}>
                    <label className={styles.label}>{t('create.fields.type')}<InfoHint text={t('fieldHints.projectType', { ns: 'common' })} /></label>
                    <div className={styles.selectWrapper}>
                        {catalogLoading ? (
                            <div className={styles.loadingContainer}>
                                <IconLoader2 className={styles.loadingIcon}/>
                                <span>{t('create.fields.loadingTypes')}</span>
                            </div>
                        ) : (
                            <select
                                name="projectType"
                                value={formData.projectType}
                                onChange={handleInputChange}
                                className={`${styles.input} ${formData.projectType ? styles.hasContent : ''}`}
                                required
                                disabled={projectCatalog.length === 0}
                            >
                                <option value="" disabled>{t('create.fields.typeSelect')}</option>
                                {projectCatalog
                                    .filter(item => item.industrySector === IndustrySector.GENERAL || item.industrySector === IndustrySector.BANK)
                                    .map((item) => (
                                        <option key={item.uid} value={getProjectType(item)}>
                                            {getProjectType(item)}
                                        </option>
                                    ))}
                            </select>
                        )}
                        <div className={styles.iconWrapper}>
                            <IconChevronDown color='#525866'/>
                        </div>
                    </div>
                </div>
            )}

          {!isBlankProject && (
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('create.fields.method')}<InfoHint text={t('fieldHints.method', { ns: 'common' })} /></label>
            <div className={styles.selectWrapper}>
              <select
                name="method"
                value={formData.method}
                onChange={handleInputChange}
                className={`${styles.input} ${formData.method ? styles.hasContent : ''}`}
                required
              >
                <option value="" disabled>{t('create.fields.methodSelect')}</option>
                <option value={Methods.AGILE}>{t('create.fields.methodAgile')}</option>
                <option value={Methods.CLASSIC}>{t('create.fields.methodClassic')}</option>
                <option value={Methods.HYBRID}>{t('create.fields.methodHybrid')}</option>
              </select>
              <div className={styles.iconWrapper}>
                <IconChevronDown color='#525866'/>
              </div>
            </div>
          </div>
          )}
          {!isBlankProject && (formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('create.fields.sprintDuration')}<InfoHint text={t('fieldHints.sprintDuration', { ns: 'common' })} /></label>
                <div className={styles.selectWrapper}>
                  <select
                    name="sprintFrequency"
                    value={formData.sprintFrequency}
                    onChange={handleInputChange}
                    className={`${styles.input} ${formData.sprintFrequency ? styles.hasContent : ''}`}
                    required
                  >
                    <option value="" disabled>{t('create.fields.sprintDurationSelect')}</option>
                    <option value={SprintFrequency.WEEKLY_1}>{t('create.fields.sprint1Week')}</option>
                    <option value={SprintFrequency.WEEKLY_2}>{t('create.fields.sprint2Weeks')}</option>
                    <option value={SprintFrequency.WEEKLY_3}>{t('create.fields.sprint3Weeks')}</option>
                    <option value={SprintFrequency.MONTHLY}>{t('create.fields.sprint1Month')}</option>
                    <option value={SprintFrequency.WEEKLY_5}>{t('create.fields.sprint5Weeks')}</option>
                  </select>
                  <div className={styles.iconWrapper}>
                    <IconChevronDown color='#525866'/>
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{t('create.fields.firstSprintStart')}<InfoHint text={t('fieldHints.firstSprintStart', { ns: 'common' })} /></label>
                <input
                  type="date"
                  name="firstSprintStartDate"
                  className={`${styles.input} ${formData.firstSprintStartDate ? styles.hasContent : ''}`}
                  value={formData.firstSprintStartDate}
                  onChange={handleInputChange}
                  min={formData.startTime || undefined}
                  max={formData.endTime || undefined}
                  required
                />
              </div>
            </>
          )}

          {!isBlankProject && (
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('create.fields.complexity')}<InfoHint text={t('fieldHints.complexity', { ns: 'common' })} /></label>
            <div className={styles.selectWrapper}>
              <select
                name="complexity"
                value={formData.complexity}
                onChange={handleInputChange}
                className={`${styles.input} ${formData.complexity ? styles.hasContent : ''}`}
                required
              >
                <option value="" disabled>{t('create.fields.complexitySelect')}</option>
                <option value={Complexity.NORMAL}>{t('create.fields.complexityNormal')}</option>
                <option value={Complexity.COMPLEX}>{t('create.fields.complexityComplex')}</option>
                <option value={Complexity.VERY_COMPLEX}>{t('create.fields.complexityVeryComplex')}</option>
              </select>
              <div className={styles.iconWrapper}>
                <IconChevronDown color='#525866'/>
              </div>
            </div>
          </div>
          )}

          {!isBlankProject && (
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('create.fields.startDate')}</label>
            <input
              type="date"
              name="startTime"
              className={`${styles.input} ${formData.startTime ? styles.hasContent : ''}`}
              value={formData.startTime}
              onChange={handleInputChange}
              required
            />
          </div>
          )}

          {!isBlankProject && (
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('create.fields.endDate')}</label>
            <input
              type="date"
              name="endTime"
              className={`${styles.input} ${formData.endTime ? styles.hasContent : ''}`}
              value={formData.endTime}
              onChange={handleInputChange}
              required
            />
          </div>
          )}

          {!isBlankProject && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t('create.fields.timingFactor')}
              <InfoHint text={t('timingFactorHint')} />
            </label>
            <div className={styles.selectWrapper}>
              <select
                name="timingFactor"
                value={formData.timingFactor}
                onChange={handleInputChange}
                className={`${styles.input} ${formData.timingFactor ? styles.hasContent : ''}`}
                required
              >
                <option value="" disabled>{t('create.fields.timingFactorSelect')}</option>
                <option value={TimingFactor.FIVE}>5%</option>
                <option value={TimingFactor.TEN}>10%</option>
                <option value={TimingFactor.FIFTEEN}>15%</option>
                <option value={TimingFactor.TWENTY}>20%</option>
                <option value={TimingFactor.TWENTY_FIVE}>25%</option>
                <option value={TimingFactor.THIRTY}>30%</option>
                <option value={TimingFactor.THIRTY_FIVE}>35%</option>
                <option value={TimingFactor.FORTY}>40%</option>
                <option value={TimingFactor.FORTY_FIVE}>45%</option>
                <option value={TimingFactor.FIFTY}>50%</option>
              </select>
              <div className={styles.iconWrapper}>
                <IconChevronDown color='#525866'/>
              </div>
            </div>
          </div>
          )}

            {!selectedTemplateId && (
                <div className={styles.formGroup}>
                    <label className={styles.label}>{t('create.fields.icon')}</label>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/png,image/jpeg,image/svg+xml"
                        onChange={handleFileChange}
                        style={{display: 'none'}}
                    />
                    <div
                        className={`${styles.fileUploadContainer} ${formData.icon ? styles.hasFile : ''}`}
                        onClick={() => {
                            if (!formData.icon && selectedTemplateId == null) {
                                fileInputRef.current?.click();
                            }
                        }}
                    >
                        {formData.icon ? (
                            <>
                                <span className={styles.fileName}>{formData.icon.name}</span>
                                <div className={styles.iconContainer}>
                                    <div onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        handleRemoveFile();
                                    }}>
                                        <IconX className={styles.deleteIcon} color='#525866'/>
                                    </div>
                                    <IconFileUpload className={styles.uploadIcon} color='#525866'/>
                                </div>
                            </>
                        ) : (
                            <>
                                <span className={styles.fileName}>{t('create.fields.iconUpload')}</span>
                                <div className={styles.iconContainer}>
                                    <IconFileUpload className={styles.uploadIcon} color='#525866'/>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={styles.fileInfo}>
                        {formData.icon ? (
                            <IconCheck className={styles.infoIcon} color='#525866'/>
                        ) : (
                            <IconFileInfo className={styles.infoIcon} color='#525866'/>
                        )}
                        <span className={styles.infoText}>
                  {t('create.fields.iconFormat')}
                </span>
                    </div>
                </div>
            )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose}>
            <span>{t('create.buttons.cancel')}</span>
          </button>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            <IconPlus color={isSubmitDisabled ? '#898989' : '#FFFFFF'} />
              <span>
              {selectedTemplateId
                  ? (instantiateTemplateMutation.isPending ? t('create.buttons.creatingFromTemplate') : t('create.buttons.createFromTemplate'))
                  : (createProjectMutation.isPending ? t('create.buttons.creating') : t('create.buttons.create'))
              }
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectPopup;
